import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button } from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { FormFieldWithLabelFactory } from '../../../components/Form/FormFieldAndLabelFactory';
import { KpiPeriod } from '../../../data-models/company-financials.data-model';
import { schemaToFormFields } from '../../../util/schema-utils';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { IFormField } from '../../../view-models/form.view-model';
import { useMprColumnConfigs } from './useMprColumnConfigs';
import {
  MprCustomFieldConfigFormData,
  MprMetricsConfigFormData,
  ReportingPeriodType,
} from './MultiPeriodReporting.schemas';
import {
  customFieldColumnFormDataToConfig,
  metricsColumnFormDataToConfig,
} from './multiPeriodReportingUtils';

type MprColumnsConfigFormData = MprMetricsConfigFormData | MprCustomFieldConfigFormData;

interface IMprMetricsAndCustomFieldColumnsFormProps {
  schema: ObjectSchema<MprMetricsConfigFormData> | ObjectSchema<MprCustomFieldConfigFormData>;
}

export function MprMetricsAndCustomFieldColumnsForm({ schema }: IMprMetricsAndCustomFieldColumnsFormProps) {
  const { setDraftColumnConfigs } = useMprColumnConfigs();

  const methods = useForm<MprColumnsConfigFormData>({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema as ObjectSchema<MprColumnsConfigFormData>),
  });

  const [rerender, setRerender] = useState(0);
  const onAdd = useCallback(
    async (data: MprMetricsConfigFormData | MprCustomFieldConfigFormData) => {
      const isValid = await methods.trigger();
      if (!isValid) {
        captureFormValidationError('MPR fields settings', methods.formState.errors);
        return;
      }
      const columnConfigs = isMetricsColumnFormData(data)
        ? metricsColumnFormDataToConfig(data as MprMetricsConfigFormData)
        : customFieldColumnFormDataToConfig(data as MprCustomFieldConfigFormData);
      setDraftColumnConfigs((prev) => (prev ?? []).concat(columnConfigs));
      methods.reset(schema.getDefault());
      setRerender((prev) => prev + 1);
    },
    [methods, schema, setDraftColumnConfigs]
  );
  const commonFields = (
    isMetricsColumnFormData(schema.getDefault()) ? ['columnPaths', 'type'] : ['customFieldIds', 'type']
  ) as (keyof MprMetricsConfigFormData)[] | (keyof MprCustomFieldConfigFormData)[];

  return (
    <FormProvider {...methods} key={rerender}>
      <Box>
        <Box display={'grid'} alignContent={'start'} sx={{ '.field-label': { marginBottom: '-0.5rem' } }}>
          <MprColumnSettingsFields schema={schema} commonFields={commonFields} />
        </Box>
        <Button
          startIcon={<AddIcon />}
          variant={'contained'}
          color={'secondary'}
          onClick={methods.handleSubmit(onAdd)}
          size={'medium'}
          sx={{ width: '100%', position: 'absolute', bottom: 0 }}
        >
          Add
        </Button>
      </Box>
    </FormProvider>
  );
}

function MprColumnSettingsFields({
  schema,
  commonFields,
}: {
  schema: ObjectSchema<MprColumnsConfigFormData>;
  commonFields: (keyof MprMetricsConfigFormData)[] | (keyof MprCustomFieldConfigFormData)[];
}) {
  const fieldsMap = useMemo(() => {
    return new Map(schemaToFormFields(schema).map((field) => [field.key, field]));
  }, [schema]);
  const dynamicFields = useDynamicMprColumnConfigFields(
    fieldsMap as Map<keyof MprColumnsConfigFormData, IFormField<unknown>>,
    schema
  );
  return (
    <Fragment>
      {commonFields.map((key) => {
        return <FormFieldWithLabelFactory key={key as string} formField={fieldsMap.get(key as string)!} />;
      })}
      {dynamicFields.map((field) => {
        return <FormFieldWithLabelFactory key={field.key!} formField={field} />;
      })}
    </Fragment>
  );
}

function useDynamicMprColumnConfigFields(
  fieldsMap: Map<keyof MprColumnsConfigFormData, IFormField<unknown>>,
  schema: ObjectSchema<MprColumnsConfigFormData>
) {
  const { setValue, watch } = useFormContext<MprColumnsConfigFormData>();
  const [fields, setFields] = useState<IFormField<unknown>[]>(() =>
    getInitialMetricsDynamicFields(schema, fieldsMap)
  );

  useEffect(() => {
    const subscription = watch((data, { name, type: eventType }) => {
      if (eventType !== 'change' || name !== 'type') {
        return;
      }
      const { type } = data;

      const yearField = { ...fieldsMap.get('year')! };
      const monthField = { ...fieldsMap.get('month')! };
      const relativeDistanceField = { ...fieldsMap.get('relativeDistance')! };
      const result = [];

      if (type === ReportingPeriodType.absolute) {
        result.push(monthField, yearField);
        setValue('relativeDistance', null);
      } else if (type === ReportingPeriodType.relative) {
        setValue('relativeDistance', [0]);
        setValue('month', null);
        setValue('year', null);
        result.push(relativeDistanceField);
      }
      setFields(result);
    });

    return () => {
      subscription.unsubscribe();
    };
  });

  return fields;
}

function getInitialMetricsDynamicFields(
  schema: ObjectSchema<MprColumnsConfigFormData>,
  fieldsMap: Map<keyof MprColumnsConfigFormData, IFormField<unknown>>
) {
  const initialValues = schema.getDefault();
  initialValues.period = KpiPeriod.quarter;
  const result: IFormField<unknown>[] = [];

  if (initialValues.type === ReportingPeriodType.relative) {
    result.push(fieldsMap.get('relativeDistance')!);
  } else if (initialValues.type === ReportingPeriodType.absolute) {
    const monthField = fieldsMap.get('month')!;
    const yearField = fieldsMap.get('year')!;
    result.push(monthField, yearField);
  }
  return result;
}

function isMetricsColumnFormData(config: MprColumnsConfigFormData): config is MprMetricsConfigFormData {
  return 'columnPaths' in config;
}
